'use client';

import React, { useState, useEffect, useMemo } from 'react';
import {
  CalendarDays, ChevronLeft, ChevronRight, Loader2, AlertCircle, Globe,
  TrendingUp,
} from 'lucide-react';
import { getEconomicCalendar, getStockHistory, type EconomicEvent, type OHLCV } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { useTimezone } from '@/hooks/useTimezone';
import { getETDate, getETTimeParts, addEtDays } from '@/lib/marketTime';
import { EventReactionWindow, type EventReactionEvent } from '@/components/shared/EventReactionWindow';

// ── Date helpers ──────────────────────────────────────────────────────────────

function getWeekRange(weekOffset: number): { from: Date; to: Date } {
  // Anchor the trading week on the ET calendar, not the browser-local clock —
  // a US-market calendar's Mon–Fri must not shift for evening / non-ET users.
  const todayEt = getETDate(new Date());
  const { weekday } = getETTimeParts(new Date()); // 0=Sun … 6=Sat
  const backToMonday = weekday === 0 ? 6 : weekday - 1;
  const mondayEt = addEtDays(todayEt, -backToMonday + weekOffset * 7);
  const fridayEt = addEtDays(mondayEt, 4);
  // Anchor at UTC noon so toYMD (UTC) and the UTC-rendered week label both
  // read the intended ET calendar date.
  return { from: new Date(mondayEt + 'T12:00:00Z'), to: new Date(fridayEt + 'T12:00:00Z') };
}

function toYMD(d: Date): string {
  return d.toISOString().split('T')[0];
}

function formatWeekLabel(from: Date, to: Date): string {
  // from/to are UTC-noon-anchored ET calendar dates — render in UTC so the
  // label shows those exact dates. Rendering in the viewer tz shifts the
  // label a day for far-east (UTC+12/+13) zones.
  const startFmt = from.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const endFmt = to.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  return `${startFmt} – ${endFmt}`;
}

function formatDayHeader(dateStr: string, tz: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: tz });
}

function isToday(dateStr: string): boolean {
  return dateStr === getETDate(new Date());
}

// ── Country flags ─────────────────────────────────────────────────────────────

const COUNTRY_FLAGS: Record<string, string> = {
  US: '\u{1F1FA}\u{1F1F8}',
  UK: '\u{1F1EC}\u{1F1E7}',
  GB: '\u{1F1EC}\u{1F1E7}',
  EU: '\u{1F1EA}\u{1F1FA}',
  JP: '\u{1F1EF}\u{1F1F5}',
  CN: '\u{1F1E8}\u{1F1F3}',
  CA: '\u{1F1E8}\u{1F1E6}',
  AU: '\u{1F1E6}\u{1F1FA}',
  DE: '\u{1F1E9}\u{1F1EA}',
  FR: '\u{1F1EB}\u{1F1F7}',
  IN: '\u{1F1EE}\u{1F1F3}',
  KR: '\u{1F1F0}\u{1F1F7}',
  BR: '\u{1F1E7}\u{1F1F7}',
  CH: '\u{1F1E8}\u{1F1ED}',
};

function getFlag(country: string): string {
  return COUNTRY_FLAGS[country?.toUpperCase()] ?? '\u{1F30D}';
}

// ── Impact badge ──────────────────────────────────────────────────────────────

function ImpactBadge({ impact }: { impact: EconomicEvent['impact'] }) {
  const styles = {
    High: 'bg-red-500/15 text-red-400 border-red-500/20',
    Medium: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
    Low: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  };
  return (
    <span className={cn('inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border', styles[impact] ?? 'bg-slate-700 text-fg-muted border-slate-600')}>
      {impact}
    </span>
  );
}

// ── Data cell ─────────────────────────────────────────────────────────────────

function DataCell({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="text-center min-w-[60px]">
      <div className="text-[10px] text-fg-subtle uppercase tracking-wide mb-0.5">{label}</div>
      <div className={cn('text-sm font-mono', value ? 'text-slate-200' : 'text-slate-600')}>
        {value ?? '--'}
      </div>
    </div>
  );
}

// ── Loading skeleton ──────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      {Array.from({ length: 3 }).map((_, di) => (
        <div key={di} className="bg-card-bg border border-default rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-default">
            <div className="h-5 w-40 bg-border-default rounded animate-pulse" />
          </div>
          <div className="divide-y divide-border-default/50">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-3">
                <div className="w-12 h-4 bg-border-default rounded animate-pulse" />
                <div className="flex-1 h-4 bg-border-default rounded animate-pulse" />
                <div className="w-10 h-4 bg-border-default rounded animate-pulse" />
                <div className="w-14 h-5 bg-border-default rounded-full animate-pulse" />
                <div className="w-12 h-4 bg-border-default rounded animate-pulse" />
                <div className="w-12 h-4 bg-border-default rounded animate-pulse" />
                <div className="w-12 h-4 bg-border-default rounded animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── SP500 Reaction tab — event-type matchers + reusable component ────────
// Each entry: a friendly label + a matcher that picks the right events
// out of Finnhub's `event` strings (e.g. "CPI m/m", "FOMC Statement").
// Hardcoded short list to start; extend as Dan flags useful releases.

type ReactionEventType = 'CPI' | 'FOMC' | 'NFP' | 'PPI' | 'Retail Sales';

const REACTION_EVENT_MATCHERS: Record<ReactionEventType, (name: string) => boolean> = {
  CPI: (n) => /\bCPI\b|Consumer Price Index/i.test(n),
  FOMC: (n) => /\bFOMC\b|Fed (?:Funds|Interest Rate|Statement)|Federal Reserve/i.test(n),
  NFP: (n) => /Non-?Farm Payrolls?|\bNFP\b/i.test(n),
  PPI: (n) => /\bPPI\b|Producer Price Index/i.test(n),
  'Retail Sales': (n) => /Retail Sales/i.test(n),
};

function shortEventDate(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!Number.isFinite(y)) return ymd;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

// ── Main Page ─────────────────────────────────────────────────────────────────

type Tab = 'calendar' | 'reaction';

export default function EconomicCalendarPage() {
  const tz = useTimezone();
  const [tab, setTab] = useState<Tab>('calendar');
  const [weekOffset, setWeekOffset] = useState(0);
  const [events, setEvents] = useState<EconomicEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { from, to } = useMemo(() => getWeekRange(weekOffset), [weekOffset]);
  const weekLabel = useMemo(() => formatWeekLabel(from, to), [from, to]);

  useEffect(() => {
    if (tab !== 'calendar') return;
    setLoading(true);
    setError('');
    getEconomicCalendar(toYMD(from), toYMD(to))
      .then((data) => {
        setEvents(data);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load economic calendar data.');
        setLoading(false);
      });
  }, [from, to, tab]);

  // Group events by date
  const grouped = useMemo(() => {
    const map: Record<string, EconomicEvent[]> = {};
    events.forEach((e) => {
      if (!map[e.date]) map[e.date] = [];
      map[e.date].push(e);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [events]);

  return (
    <div className="min-h-screen bg-surface text-white">
      <div className="px-2 sm:px-3 lg:px-4 py-8">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                <CalendarDays size={18} className="text-amber-400" />
              </div>
              <h1 className="text-2xl font-bold text-white">Economic Calendar</h1>
            </div>
            <p className="text-fg-muted text-sm ml-12">
              Key economic events and data releases{tab === 'calendar' ? ` — ${weekLabel}` : ''}
            </p>
          </div>
        </div>

        {/* Tab strip */}
        <div className="flex gap-1 mb-4 border-b border-default">
          <TabButton active={tab === 'calendar'} onClick={() => setTab('calendar')} icon={<CalendarDays size={14} />}>
            Calendar
          </TabButton>
          <TabButton active={tab === 'reaction'} onClick={() => setTab('reaction')} icon={<TrendingUp size={14} />}>
            SP500 Reaction
          </TabButton>
        </div>

        {tab === 'reaction' && <ReactionTab />}

        {tab === 'calendar' && (
        <>
        {/* Week navigation */}
        <div className="bg-card-bg border border-default rounded-xl p-4 mb-6">
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => setWeekOffset((o) => o - 1)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface border border-default text-fg-muted hover:text-white hover:border-slate-500 transition-colors text-sm"
            >
              <ChevronLeft size={16} />
              <span className="hidden sm:inline">Prev Week</span>
            </button>

            <button
              onClick={() => setWeekOffset(0)}
              className={cn(
                'px-4 py-1.5 rounded-lg text-sm font-medium transition-colors',
                weekOffset === 0
                  ? 'bg-amber-500/10 border border-amber-500/30 text-amber-400'
                  : 'bg-surface border border-default text-fg-muted hover:text-white hover:border-slate-500',
              )}
            >
              Current Week
            </button>

            <button
              onClick={() => setWeekOffset((o) => o + 1)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface border border-default text-fg-muted hover:text-white hover:border-slate-500 transition-colors text-sm"
            >
              <span className="hidden sm:inline">Next Week</span>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl mb-6 text-red-400">
            <AlertCircle size={18} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Loading */}
        {loading && <LoadingSkeleton />}

        {/* Empty state */}
        {!loading && !error && grouped.length === 0 && (
          <div className="bg-card-bg border border-default rounded-xl">
            <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
              <div className="w-14 h-14 rounded-full bg-card-bg border border-default flex items-center justify-center">
                <Globe size={22} className="text-slate-600" />
              </div>
              <div>
                <p className="text-fg-muted font-medium">No economic events this week</p>
                <p className="text-slate-600 text-sm mt-1">Try navigating to a different week.</p>
              </div>
            </div>
          </div>
        )}

        {/* Grouped events by date */}
        {!loading && grouped.length > 0 && (
          <div className="space-y-4">
            {grouped.map(([dateStr, dayEvents]) => (
              <div
                key={dateStr}
                className={cn(
                  'bg-card-bg border rounded-xl overflow-hidden',
                  isToday(dateStr) ? 'border-amber-500/30' : 'border-default',
                )}
              >
                {/* Day header */}
                <div
                  className={cn(
                    'px-5 py-3 border-b flex items-center justify-between',
                    isToday(dateStr)
                      ? 'bg-amber-500/10 border-amber-500/20'
                      : 'bg-surface/40 border-default',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <CalendarDays size={14} className={isToday(dateStr) ? 'text-amber-400' : 'text-fg-subtle'} />
                    <span className={cn('text-sm font-semibold', isToday(dateStr) ? 'text-amber-300' : 'text-slate-300')}>
                      {formatDayHeader(dateStr, tz)}
                    </span>
                    {isToday(dateStr) && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500 text-black">TODAY</span>
                    )}
                  </div>
                  <span className="text-xs text-fg-subtle">{dayEvents.length} event{dayEvents.length !== 1 ? 's' : ''}</span>
                </div>

                {/* Desktop events */}
                <div className="hidden md:block">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-default/50 bg-surface/20">
                        <th className="text-left px-5 py-2 text-xs font-medium text-fg-subtle uppercase tracking-wide w-16">Time</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-fg-subtle uppercase tracking-wide w-10"></th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-fg-subtle uppercase tracking-wide">Event</th>
                        <th className="text-center px-3 py-2 text-xs font-medium text-fg-subtle uppercase tracking-wide w-20">Impact</th>
                        <th className="text-center px-3 py-2 text-xs font-medium text-fg-subtle uppercase tracking-wide w-20">Forecast</th>
                        <th className="text-center px-3 py-2 text-xs font-medium text-fg-subtle uppercase tracking-wide w-20">Previous</th>
                        <th className="text-center px-5 py-2 text-xs font-medium text-fg-subtle uppercase tracking-wide w-20">Actual</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dayEvents.map((ev, idx) => (
                        <tr
                          key={ev.id}
                          className={cn(
                            'border-b border-default/30 transition-colors hover:bg-card-hover-bg',
                            idx % 2 === 0 ? 'bg-transparent' : 'bg-surface/10',
                          )}
                        >
                          <td className="px-5 py-3 text-fg-muted font-mono text-xs">{ev.time || '--'}</td>
                          <td className="px-3 py-3 text-base">{getFlag(ev.country)}</td>
                          <td className="px-3 py-3 text-slate-200 font-medium">{ev.event}</td>
                          <td className="px-3 py-3 text-center"><ImpactBadge impact={ev.impact} /></td>
                          <td className="px-3 py-3 text-center text-slate-300 font-mono text-sm">{ev.forecast ?? '--'}</td>
                          <td className="px-3 py-3 text-center text-fg-muted font-mono text-sm">{ev.previous ?? '--'}</td>
                          <td className="px-5 py-3 text-center font-mono text-sm">
                            <span className={ev.actual ? 'text-white font-semibold' : 'text-slate-600'}>
                              {ev.actual ?? '--'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile events */}
                <div className="md:hidden divide-y divide-border-default/40">
                  {dayEvents.map((ev) => (
                    <div key={ev.id} className="px-4 py-3 hover:bg-card-hover-bg transition-colors">
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="text-base flex-shrink-0">{getFlag(ev.country)}</span>
                          <span className="text-slate-200 text-sm font-medium truncate">{ev.event}</span>
                        </div>
                        <ImpactBadge impact={ev.impact} />
                      </div>
                      <div className="flex items-center gap-4 text-xs">
                        <span className="text-fg-subtle font-mono">{ev.time || '--'}</span>
                        <DataCell label="Fcst" value={ev.forecast} />
                        <DataCell label="Prev" value={ev.previous} />
                        <DataCell label="Act" value={ev.actual} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 text-xs text-slate-600">
          Event times shown in ET. Data subject to revision.
        </div>
        </>
        )}
      </div>
    </div>
  );
}

// ── Tab strip + reaction tab body ─────────────────────────────────────────────

function TabButton({
  active, onClick, icon, children,
}: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
        active
          ? 'border-amber-400 text-amber-300'
          : 'border-transparent text-fg-muted hover:text-slate-200',
      )}
    >
      {icon}
      {children}
    </button>
  );
}

function ReactionTab() {
  // Default to CPI — the most-asked-about of the bunch.
  const [eventType, setEventType] = useState<ReactionEventType>('CPI');
  const [bars, setBars] = useState<OHLCV[] | null>(null);
  const [pastEvents, setPastEvents] = useState<EconomicEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pull SPY 365d daily bars + ~365d of past econ events on mount. Both
  // are reused across event-type switches (filtering happens client-side).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const today = new Date();
    const lookback = new Date(today);
    lookback.setDate(today.getDate() - 365);
    Promise.all([
      getStockHistory('SPY', 365),
      getEconomicCalendar(toYMD(lookback), toYMD(today)),
    ])
      .then(([spyBars, evs]) => {
        if (cancelled) return;
        setBars(spyBars);
        setPastEvents(evs);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Failed to load SPY history or past events.');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Filter past events by the chosen type, US-country only (CPI/FOMC/NFP
  // are macro-relevant when they're US releases — non-US copies confuse
  // the chart). Keep most-recent first to match the earnings-grid order.
  const reactionEvents: EventReactionEvent[] = useMemo(() => {
    if (!eventType) return [];
    const matcher = REACTION_EVENT_MATCHERS[eventType];
    return pastEvents
      .filter((e) => e.country?.toUpperCase() === 'US' && matcher(e.event))
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 12) // cap at last 12 to keep the grid readable
      .map((e) => ({
        date: e.date,
        label: shortEventDate(e.date),
        sublabel: e.actual ? `actual ${e.actual}` : undefined,
      }));
  }, [pastEvents, eventType]);

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-fg-subtle uppercase tracking-wide font-medium mr-1">Event:</span>
        {(Object.keys(REACTION_EVENT_MATCHERS) as ReactionEventType[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setEventType(t)}
            className={cn(
              'px-3 py-1 rounded-md text-xs font-medium border transition-colors',
              eventType === t
                ? 'bg-sky-500/15 border-sky-500/40 text-sky-300'
                : 'bg-surface border-default text-fg-muted hover:text-slate-200 hover:border-slate-500',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400">
          <AlertCircle size={18} />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {loading && (
        <div className="bg-card-bg border border-default rounded-xl p-8 flex items-center justify-center text-fg-subtle text-sm gap-2">
          <Loader2 size={14} className="animate-spin" />
          Loading SPY history + past events…
        </div>
      )}

      {!loading && !error && bars && (
        <EventReactionWindow
          bars={bars}
          events={reactionEvents}
          before={5}
          after={5}
          title={`SPY around past ${eventType} releases (US, last 12)`}
          d0Label={`${eventType} release`}
          emptyMessage={`No US ${eventType} events found in the last 365 days.`}
        />
      )}

      <p className="text-xs text-slate-600">
        Each cell = SPY's daily close-to-close % change for that trading day. ★ = release day.
        Toggle EOD% / Drift% / Price in the grid header.
      </p>
    </section>
  );
}
